import React, { useState, useEffect } from "react";
import {
  Container,
  Card,
  Form,
  Button,
  Row,
  Col,
  Accordion,
  Badge,
  Alert,
  ListGroup,
  Spinner,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import {
  doc,
  getDoc,
  updateDoc,
  getFirestore,
  Timestamp,
} from "firebase/firestore";
import { useNavigate, useParams } from "react-router-dom";
import {
  FaSave,
  FaArrowLeft,
  FaPlus,
  FaTrash,
  FaClipboardList,
  FaQuestionCircle,
  FaCalendarAlt,
  FaCheckCircle,
} from "react-icons/fa";
import firebaseApp from "../../firebase/credenciales";
import { logBusinessAction } from "../../services/AuditService";

const db = getFirestore(firebaseApp);

const EditarCampanaPage = ({ usuario }) => {
  const { campanaid } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Datos de la campaña
  const [nombreCampana, setNombreCampana] = useState("");
  const [tipoEvaluacion, setTipoEvaluacion] = useState("180");
  const [fechaInicio, setFechaInicio] = useState("");
  const [fechaFin, setFechaFin] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [instrucciones, setInstrucciones] = useState("");
  const [dimensiones, setDimensiones] = useState([]);

  useEffect(() => {
    if (usuario?.ruc && campanaid) {
      cargarCampana();
    }
  }, [usuario, campanaid]);

  const cargarCampana = async () => {
    try {
      setLoading(true);
      const rucEmpresa = usuario?.ruc || "";
      const campanaRef = doc(
        db,
        `evaluaciones/${rucEmpresa}/campañas`,
        campanaid
      );
      const campanaSnap = await getDoc(campanaRef);

      if (!campanaSnap.exists()) {
        setError("Campaña no encontrada");
        setTimeout(() => navigate("/evaluaciones"), 2000);
        return;
      }

      const data = campanaSnap.data();

      setNombreCampana(data.nombre || "");
      setTipoEvaluacion(data.tipoEvaluacion || "180");
      setDescripcion(data.descripcion || "");
      setInstrucciones(data.instrucciones || "");

      // Convertir timestamps a fecha formato input
      if (data.fechaInicio) {
        const fechaInicioDate = new Date(
          data.fechaInicio.seconds * 1000
        );
        setFechaInicio(fechaInicioDate.toISOString().split("T")[0]);
      }

      if (data.fechaFin) {
        const fechaFinDate = new Date(data.fechaFin.seconds * 1000);
        setFechaFin(fechaFinDate.toISOString().split("T")[0]);
      }

      // Cargar dimensiones con IDs únicos
      const dimensionesConId = (data.dimensiones || []).map((d, index) => ({
        id: Date.now() + index,
        nombre: d.nombre || "",
        peso: d.peso || 0,
        preguntas: (d.preguntas || []).map((p, pIndex) => ({
          id: Date.now() + index * 1000 + pIndex,
          texto: p.texto || "",
          escala: p.escala || 5,
        })),
      }));

      setDimensiones(dimensionesConId);
    } catch (error) {
      console.error("Error al cargar campaña:", error);
      setError("Error al cargar la campaña");
    } finally {
      setLoading(false);
    }
  };

  const agregarDimension = () => {
    const nuevaDimension = {
      id: Date.now(),
      nombre: "",
      peso: 0,
      preguntas: [],
    };
    setDimensiones([...dimensiones, nuevaDimension]);
  };

  const eliminarDimension = (id) => {
    if (dimensiones.length > 1) {
      setDimensiones(dimensiones.filter((d) => d.id !== id));
    } else {
      setError("Debe haber al menos una dimensión de evaluación");
      setTimeout(() => setError(""), 3000);
    }
  };

  const actualizarDimension = (id, campo, valor) => {
    setDimensiones(
      dimensiones.map((d) => (d.id === id ? { ...d, [campo]: valor } : d))
    );
  };

  const agregarPregunta = (dimensionId) => {
    setDimensiones(
      dimensiones.map((d) => {
        if (d.id === dimensionId) {
          return {
            ...d,
            preguntas: [
              ...(d.preguntas || []),
              { id: Date.now(), texto: "", escala: 5 },
            ],
          };
        }
        return d;
      })
    );
  };

  const eliminarPregunta = (dimensionId, preguntaId) => {
    setDimensiones(
      dimensiones.map((d) => {
        if (d.id === dimensionId) {
          return {
            ...d,
            preguntas: d.preguntas.filter((p) => p.id !== preguntaId),
          };
        }
        return d;
      })
    );
  };

  const actualizarPregunta = (dimensionId, preguntaId, campo, valor) => {
    setDimensiones(
      dimensiones.map((d) => {
        if (d.id === dimensionId) {
          return {
            ...d,
            preguntas: d.preguntas.map((p) =>
              p.id === preguntaId ? { ...p, [campo]: valor } : p
            ),
          };
        }
        return d;
      })
    );
  };

  const validarFormulario = () => {
    if (!nombreCampana.trim()) {
      setError("El nombre de la campaña es obligatorio");
      return false;
    }

    if (!fechaInicio || !fechaFin) {
      setError("Las fechas de inicio y fin son obligatorias");
      return false;
    }

    if (new Date(fechaFin) <= new Date(fechaInicio)) {
      setError("La fecha de fin debe ser posterior a la fecha de inicio");
      return false;
    }

    if (dimensiones.length === 0) {
      setError("Debe haber al menos una dimensión de evaluación");
      return false;
    }

    const pesoTotal = dimensiones.reduce(
      (sum, d) => sum + parseFloat(d.peso || 0),
      0
    );
    if (Math.abs(pesoTotal - 100) > 0.1) {
      setError("La suma de los pesos de las dimensiones debe ser 100%");
      return false;
    }

    for (let dim of dimensiones) {
      if (!dim.nombre.trim()) {
        setError("Todas las dimensiones deben tener un nombre");
        return false;
      }
      if (!dim.preguntas || dim.preguntas.length === 0) {
        setError(
          `La dimensión "${dim.nombre}" debe tener al menos una pregunta`
        );
        return false;
      }
      for (let preg of dim.preguntas) {
        if (!preg.texto.trim()) {
          setError(`Todas las preguntas en "${dim.nombre}" deben tener texto`);
          return false;
        }
      }
    }

    return true;
  };

  const guardarCambios = async () => {
    setError("");
    setSuccess("");

    if (!validarFormulario()) {
      return;
    }

    try {
      setSaving(true);
      const rucEmpresa = usuario?.ruc || "";
      const campanaRef = doc(
        db,
        `evaluaciones/${rucEmpresa}/campañas`,
        campanaid
      );

      const datosActualizados = {
        nombre: nombreCampana.trim(),
        tipoEvaluacion: tipoEvaluacion,
        descripcion: descripcion.trim(),
        instrucciones: instrucciones.trim(),
        fechaInicio: Timestamp.fromDate(new Date(fechaInicio)),
        fechaFin: Timestamp.fromDate(new Date(fechaFin)),
        dimensiones: dimensiones.map((d) => ({
          nombre: d.nombre.trim(),
          peso: parseFloat(d.peso),
          preguntas: (d.preguntas || []).map((p) => ({
            texto: p.texto.trim(),
            escala: parseInt(p.escala),
          })),
        })),
        ultimaActualizacion: Timestamp.now(),
      };

      await updateDoc(campanaRef, datosActualizados);

      // AUDITORÍA EDICIÓN CAMPAÑA
      try {
        await logBusinessAction("EDICION_CAMPANA_EVALUACION", "GestionEvaluacionDesempeno", {
          id: campanaid,
          nombre: nombreCampana.trim(),
          tipoEvaluacion: tipoEvaluacion,
          fechaInicio: fechaInicio,
          fechaFin: fechaFin
        }, usuario);
      } catch (e) {
        console.warn("No se pudo registrar la auditoría de edición de campaña de evaluación", e);
      }

      setSuccess("¡Campaña actualizada exitosamente!");
      setTimeout(() => {
        navigate(`/evaluaciones/campana/${campanaid}`);
      }, 1500);
    } catch (error) {
      console.error("Error al actualizar campaña:", error);
      setError("Error al actualizar la campaña. Inténtalo de nuevo.");
    } finally {
      setSaving(false);
    }
  };

  const pesoTotal = dimensiones.reduce(
    (sum, d) => sum + parseFloat(d.peso || 0),
    0
  );

  if (loading) {
    return (
      <div
        className="min-vh-100 d-flex justify-content-center align-items-center"
        style={{
          background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
        }}
      >
        <div className="text-center">
          <Spinner
            animation="border"
            variant="primary"
            style={{ width: "3rem", height: "3rem" }}
          />
          <p className="mt-3 fw-semibold">Cargando campaña...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-vh-100"
      style={{
        background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
        padding: "20px 0",
      }}
    >
      <Container fluid style={{ width: "90%", maxWidth: "100%" }}>
        {/* Indicador de guardado */}
        {saving && (
          <div
            className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center bg-dark bg-opacity-75"
            style={{ zIndex: 1080 }}
          >
            <div className="text-center">
              <Spinner
                animation="border"
                variant="light"
                style={{ width: "3rem", height: "3rem" }}
              />
              <p className="text-white mt-3 mb-0">Guardando cambios...</p>
            </div>
          </div>
        )}

        <Card
          className="shadow-lg border-0"
          style={{ borderRadius: "15px", overflow: "hidden" }}
        >
          {/* Header */}
          <Card.Header
            className="text-white py-4"
            style={{
              background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
              borderBottom: "3px solid #ff8a80",
            }}
          >
            <div className="text-center">
              <h2 className="fw-bold mb-1">
                <FaClipboardList className="me-3" />
                Editar Campaña de Evaluación
              </h2>
              <p className="mb-0 opacity-75">
                Modifica los datos de la campaña existente
              </p>
            </div>
          </Card.Header>

          <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
            {/* Alertas */}
            {error && (
              <Alert
                variant="danger"
                dismissible
                onClose={() => setError("")}
                className="mb-4"
              >
                <strong>Error:</strong> {error}
              </Alert>
            )}

            {success && (
              <Alert variant="success" className="mb-4">
                <FaCheckCircle className="me-2" />
                {success}
              </Alert>
            )}

            {/* Información general */}
            <Card
              className="border-0 shadow-sm mb-4"
              style={{ borderRadius: "15px" }}
            >
              <Card.Header
                className="text-white py-3"
                style={{
                  background:
                    "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                  borderRadius: "15px 15px 0 0",
                }}
              >
                <h5 className="mb-0 fw-semibold">
                  <FaClipboardList className="me-2" />
                  Información General
                </h5>
              </Card.Header>
              <Card.Body className="p-4">
                <Row className="g-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-semibold">
                        Nombre de la Campaña *
                      </Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Ej: Evaluación Anual 2024"
                        value={nombreCampana}
                        onChange={(e) => setNombreCampana(e.target.value)}
                        style={{ borderRadius: "10px" }}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-semibold">
                        Tipo de Evaluación *
                      </Form.Label>
                      <Form.Select
                        value={tipoEvaluacion}
                        onChange={(e) => setTipoEvaluacion(e.target.value)}
                        style={{ borderRadius: "10px" }}
                      >
                        <option value="90">90° (Autoevaluación)</option>
                        <option value="180">
                          180° (Superior + Autoevaluación)
                        </option>
                        <option value="360">
                          360° (Superior + Pares + Subordinados + Auto)
                        </option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-semibold">
                        <FaCalendarAlt className="me-1" />
                        Fecha de Inicio *
                      </Form.Label>
                      <Form.Control
                        type="date"
                        value={fechaInicio}
                        onChange={(e) => setFechaInicio(e.target.value)}
                        style={{ borderRadius: "10px" }}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-semibold">
                        <FaCalendarAlt className="me-1" />
                        Fecha de Fin *
                      </Form.Label>
                      <Form.Control
                        type="date"
                        value={fechaFin}
                        onChange={(e) => setFechaFin(e.target.value)}
                        style={{ borderRadius: "10px" }}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label className="fw-semibold">
                        Descripción
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        placeholder="Describe el propósito de esta evaluación"
                        value={descripcion}
                        onChange={(e) => setDescripcion(e.target.value)}
                        style={{ borderRadius: "10px" }}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label className="fw-semibold">
                        Instrucciones para Evaluadores
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        placeholder="Instrucciones que verán los evaluadores al iniciar"
                        value={instrucciones}
                        onChange={(e) => setInstrucciones(e.target.value)}
                        style={{ borderRadius: "10px" }}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Dimensiones de evaluación */}
            <Card
              className="border-0 shadow-sm mb-4"
              style={{ borderRadius: "15px" }}
            >
              <Card.Header
                className="text-white py-3"
                style={{
                  background:
                    "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                  borderRadius: "15px 15px 0 0",
                }}
              >
                <div className="d-flex align-items-center justify-content-between">
                  <h5 className="mb-0 fw-semibold">
                    <FaQuestionCircle className="me-2" />
                    Dimensiones de Evaluación
                  </h5>
                  <div className="d-flex align-items-center gap-3">
                    <Badge
                      bg={pesoTotal === 100 ? "success" : "warning"}
                      style={{ fontSize: "0.9rem" }}
                    >
                      Peso Total: {pesoTotal.toFixed(1)}%
                    </Badge>
                    <Button
                      variant="light"
                      size="sm"
                      onClick={agregarDimension}
                      className="d-flex align-items-center gap-2"
                    >
                      <FaPlus size="12px" />
                      Agregar Dimensión
                    </Button>
                  </div>
                </div>
              </Card.Header>
              <Card.Body className="p-4">
                {pesoTotal !== 100 && (
                  <Alert variant="warning" className="mb-3">
                    <FaQuestionCircle className="me-2" />
                    El peso total debe sumar 100%. Actualmente es{" "}
                    {pesoTotal.toFixed(1)}%
                  </Alert>
                )}

                <Accordion defaultActiveKey="0">
                  {dimensiones.map((dimension, dimIndex) => (
                    <Accordion.Item
                      eventKey={String(dimIndex)}
                      key={dimension.id}
                      className="mb-3"
                      style={{ borderRadius: "10px", overflow: "hidden" }}
                    >
                      <Accordion.Header>
                        <div className="d-flex align-items-center gap-3 w-100">
                          <Badge
                            style={{
                              background:
                                "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                            }}
                          >
                            Dimensión {dimIndex + 1}
                          </Badge>
                          <span className="fw-bold">
                            {dimension.nombre || "Sin nombre"}
                          </span>
                          <Badge bg="info">{dimension.peso}%</Badge>
                          <Badge bg="secondary">
                            {(dimension.preguntas || []).length} preguntas
                          </Badge>
                        </div>
                      </Accordion.Header>
                      <Accordion.Body className="bg-light">
                        <Row className="g-3 mb-3">
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label className="fw-semibold">
                                Nombre de la Dimensión *
                              </Form.Label>
                              <Form.Control
                                type="text"
                                placeholder="Ej: Desempeño Técnico"
                                value={dimension.nombre}
                                onChange={(e) =>
                                  actualizarDimension(
                                    dimension.id,
                                    "nombre",
                                    e.target.value
                                  )
                                }
                                style={{ borderRadius: "8px" }}
                              />
                            </Form.Group>
                          </Col>
                          <Col md={4}>
                            <Form.Group>
                              <Form.Label className="fw-semibold">
                                Peso (%) *
                              </Form.Label>
                              <Form.Control
                                type="number"
                                min="0"
                                max="100"
                                step="0.1"
                                value={dimension.peso}
                                onChange={(e) =>
                                  actualizarDimension(
                                    dimension.id,
                                    "peso",
                                    e.target.value
                                  )
                                }
                                style={{ borderRadius: "8px" }}
                              />
                            </Form.Group>
                          </Col>
                          <Col md={2} className="d-flex align-items-end">
                            {dimensiones.length > 1 && (
                              <Button
                                variant="outline-danger"
                                className="w-100"
                                onClick={() => eliminarDimension(dimension.id)}
                                style={{ borderRadius: "8px" }}
                              >
                                <FaTrash />
                              </Button>
                            )}
                          </Col>
                        </Row>

                        {/* Preguntas */}
                        <div className="mb-3">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <h6 className="mb-0 fw-semibold">Preguntas</h6>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => agregarPregunta(dimension.id)}
                              style={{
                                borderRadius: "8px",
                                borderColor: "#f093fb",
                                color: "#f093fb",
                              }}
                            >
                              <FaPlus size="12px" className="me-1" />
                              Agregar Pregunta
                            </Button>
                          </div>

                          {(!dimension.preguntas ||
                            dimension.preguntas.length === 0) && (
                            <Alert variant="info" className="py-2">
                              No hay preguntas. Agrega al menos una.
                            </Alert>
                          )}

                          <ListGroup>
                            {(dimension.preguntas || []).map(
                              (pregunta, pregIndex) => (
                                <ListGroup.Item
                                  key={pregunta.id}
                                  className="mb-2"
                                  style={{ borderRadius: "8px" }}
                                >
                                  <Row className="g-2 align-items-center">
                                    <Col md={1} className="text-center">
                                      <Badge
                                        bg="secondary"
                                        style={{ fontSize: "0.8rem" }}
                                      >
                                        {pregIndex + 1}
                                      </Badge>
                                    </Col>
                                    <Col md={8}>
                                      <Form.Control
                                        type="text"
                                        placeholder="Escribe la pregunta aquí"
                                        value={pregunta.texto}
                                        onChange={(e) =>
                                          actualizarPregunta(
                                            dimension.id,
                                            pregunta.id,
                                            "texto",
                                            e.target.value
                                          )
                                        }
                                        style={{ borderRadius: "6px" }}
                                      />
                                    </Col>
                                    <Col md={2}>
                                      <Form.Select
                                        value={pregunta.escala}
                                        onChange={(e) =>
                                          actualizarPregunta(
                                            dimension.id,
                                            pregunta.id,
                                            "escala",
                                            e.target.value
                                          )
                                        }
                                        size="sm"
                                        style={{ borderRadius: "6px" }}
                                      >
                                        <option value="3">Escala 1-3</option>
                                        <option value="5">Escala 1-5</option>
                                        <option value="10">Escala 1-10</option>
                                      </Form.Select>
                                    </Col>
                                    <Col md={1}>
                                      <Button
                                        variant="outline-danger"
                                        size="sm"
                                        onClick={() =>
                                          eliminarPregunta(
                                            dimension.id,
                                            pregunta.id
                                          )
                                        }
                                        style={{ borderRadius: "6px" }}
                                      >
                                        <FaTrash size="12px" />
                                      </Button>
                                    </Col>
                                  </Row>
                                </ListGroup.Item>
                              )
                            )}
                          </ListGroup>
                        </div>
                      </Accordion.Body>
                    </Accordion.Item>
                  ))}
                </Accordion>
              </Card.Body>
            </Card>

            {/* Botones de acción */}
            <div className="d-flex gap-3 justify-content-end">
              <Button
                variant="outline-secondary"
                size="lg"
                onClick={() => navigate(`/evaluaciones/campana/${campanaid}`)}
                className="d-flex align-items-center gap-2"
                style={{ borderRadius: "10px" }}
              >
                <FaArrowLeft />
                Cancelar
              </Button>
              <Button
                className="d-flex align-items-center gap-2 text-white"
                size="lg"
                onClick={guardarCambios}
                disabled={saving}
                style={{
                  borderRadius: "10px",
                  background:
                    "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                  border: "none",
                }}
              >
                <FaSave />
                {saving ? "Guardando..." : "Guardar Cambios"}
              </Button>
            </div>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export default EditarCampanaPage;
